import { useState, useCallback, useEffect, useRef } from 'react';
import { AppError } from '../types/error';
import { ErrorManager } from '../utils/errorManager';
import { ErrorLogger } from '../utils/errorLogger';
import { ErrorFactory } from '../utils/errorFactory';

interface UseErrorHandlingOptions {
  onError?: (error: AppError) => void;
  autoRetry?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

interface ErrorState {
  error: AppError | null;
  isRetrying: boolean;
  retryCount: number;
  canRetry: boolean;
}

/**
 * Hook for comprehensive error handling with retry logic
 */
export const useErrorHandling = (options: UseErrorHandlingOptions = {}) => {
  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    isRetrying: false,
    retryCount: 0,
    canRetry: false
  });

  const lastOperationRef = useRef<(() => Promise<any>) | undefined>(undefined);
  const {
    onError,
    autoRetry = false,
    maxRetries = 3,
    retryDelay = 1000
  } = options;

  const clearError = useCallback(() => {
    setErrorState({
      error: null,
      isRetrying: false,
      retryCount: 0,
      canRetry: false
    });
  }, []);

  const setError = useCallback((error: unknown) => {
    const appError = error instanceof Error && 'category' in error 
      ? error as AppError 
      : ErrorFactory.fromUnknown(error, 'UNKNOWN_ERROR', 'An unexpected error occurred');

    setErrorState(prev => {
      const newState = {
        ...prev,
        error: appError,
        canRetry: appError.retryable && prev.retryCount < maxRetries
      };

      return newState;
    });

    if (onError) {
      onError(appError);
    }
  }, [onError, maxRetries]);

  const retry = useCallback(async () => {
    if (!lastOperationRef.current || !errorState.canRetry) return;

    setErrorState(prev => ({
      ...prev,
      isRetrying: true
    }));

    try {
      await lastOperationRef.current();
      clearError();
    } catch (error) {
      setErrorState(prev => ({
        ...prev,
        isRetrying: false,
        retryCount: prev.retryCount + 1,
        canRetry: prev.retryCount + 1 < maxRetries
      }));
      setError(error);
    }
  }, [errorState.canRetry, maxRetries, clearError, setError]);

  const executeWithErrorHandling = useCallback(async <T>(
    operation: () => Promise<T>,
    operationName?: string
  ): Promise<T | null> => {
    lastOperationRef.current = operation;

    try {
      const result = await operation();
      clearError();
      return result;
    } catch (error) {
      setError(error);
      
      // Log the error with operation context
      if (operationName) {
        ErrorLogger.logUserAction(
          (error as any)?.code || 'UNKNOWN_ERROR',
          `operation_failed_${operationName}`
        );
      }
      
      return null;
    }
  }, [clearError, setError]);

  // Auto-retry effect
  useEffect(() => {
    if (autoRetry && errorState.error?.retryable && errorState.retryCount < maxRetries && !errorState.isRetrying) {
      const timeoutId = setTimeout(() => {
        retry();
      }, retryDelay);

      return () => clearTimeout(timeoutId);
    }
  }, [autoRetry, errorState.error, errorState.retryCount, errorState.isRetrying, maxRetries, retryDelay, retry]);

  return {
    error: errorState.error,
    isRetrying: errorState.isRetrying,
    retryCount: errorState.retryCount,
    canRetry: errorState.canRetry,
    setError,
    clearError,
    retry,
    executeWithErrorHandling
  };
};

/**
 * Hook for wrapping async operations with automatic error handling and retry
 */
export const useAsyncOperation = <T, Args extends any[]>(
  operation: (...args: Args) => Promise<T>,
  options: {
    onSuccess?: (result: T) => void;
    onError?: (error: AppError) => void;
    retryConfig?: {
      maxAttempts?: number;
      baseDelay?: number;
      exponentialBackoff?: boolean;
    };
  } = {}
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { error, setError, clearError } = useErrorHandling({
    onError: options.onError
  });

  const execute = useCallback(async (...args: Args): Promise<T | null> => {
    setIsLoading(true);
    clearError();

    try {
      const result = await ErrorManager.withRetry(
        () => operation(...args),
        {
          operationName: operation.name || 'async_operation',
          maxAttempts: options.retryConfig?.maxAttempts || 3,
          baseDelay: options.retryConfig?.baseDelay || 1000,
          exponentialBackoff: options.retryConfig?.exponentialBackoff ?? true
        }
      );

      if (options.onSuccess) {
        options.onSuccess(result);
      }

      return result;
    } catch (error) {
      setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [operation, options, setError, clearError]);

  return {
    execute,
    isLoading,
    error,
    clearError
  };
};

/**
 * Hook for handling errors in React component lifecycle
 */
export const useErrorBoundary = () => {
  const [error, setError] = useState<AppError | null>(null);

  const captureError = useCallback((error: unknown, errorInfo?: any) => {
    const appError = ErrorFactory.fromUnknown(
      error,
      'COMPONENT_ERROR',
      'A component error occurred',
      { additionalData: { operation: 'component_error' } }
    );
    
    // Add React error info to context
    if (errorInfo) {
      appError.context = {
        ...appError.context,
        additionalData: {
          ...appError.context?.additionalData,
          componentStack: errorInfo.componentStack,
          errorBoundary: true
        }
      };
    }

    setError(appError);
    ErrorLogger.logError(appError);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    captureError,
    clearError,
    hasError: error !== null
  };
};

/**
 * Hook for monitoring and handling network connectivity
 */
export const useNetworkErrorHandling = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkError, setNetworkError] = useState<AppError | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setNetworkError(null);
    };

    const handleOffline = () => {
      setIsOnline(false);
      const offlineError = ErrorFactory.createNetworkError(
        'NETWORK_OFFLINE',
        'Network connection lost',
        'Your internet connection is currently unavailable'
      );
      setNetworkError(offlineError);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const executeWhenOnline = useCallback(async <T>(
    operation: () => Promise<T>
  ): Promise<T | null> => {
    if (!isOnline) {
      const offlineError = ErrorFactory.createNetworkError(
        'NETWORK_OFFLINE',
        'No internet connection available',
        'Please check your internet connection and try again'
      );
      setNetworkError(offlineError);
      return null;
    }

    try {
      const result = await operation();
      setNetworkError(null);
      return result;
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const networkError = ErrorFactory.createNetworkError(
          'FETCH_ERROR',
          'Network request failed',
          'Failed to connect to server'
        );
        setNetworkError(networkError);
      }
      throw error;
    }
  }, [isOnline]);

  return {
    isOnline,
    networkError,
    executeWhenOnline,
    clearNetworkError: () => setNetworkError(null)
  };
};
